import { Button } from '@accure/ui';
import { Menu } from '@headlessui/react';
import { Calendar, Mail, Plus } from 'lucide-react';
import { useBoolean } from 'usehooks-ts';

import { useEmail } from '../hooks/use-email';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

export function CommunicationSidebarCreate() {
  const [email, copyEmail] = useEmail();
  const showEmailModal = useBoolean(false);
  const showMeetingModal = useBoolean(false);

  return (
    <>
      <Menu as="div" className="relative inline-block text-left">
        <Menu.Button className="rounded-full p-2 text-gray-600 hover:bg-gray-100">
          <Plus className="size-5" />
          <span className="sr-only">Create new</span>
        </Menu.Button>
        <Menu.Items className="absolute right-0 z-[100] mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md border border-gray-200 bg-white shadow-lg ring-1 ring-black/5 focus:outline-none">
          <div className="p-1">
            <Menu.Item>
              {({ active }) => (
                <button
                  className={`${
                    active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                  } group flex w-full items-center rounded-md p-2 text-sm`}
                  onClick={showEmailModal.setTrue}
                >
                  <Mail className="mr-2 size-4" />
                  Add Email
                </button>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <button
                  className={`${
                    active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                  } group flex w-full items-center rounded-md p-2 text-sm`}
                  onClick={showMeetingModal.setTrue}
                >
                  <Calendar className="mr-2 size-4" />
                  Add Meeting
                </button>
              )}
            </Menu.Item>
          </div>
        </Menu.Items>
      </Menu>

      <Dialog
        open={showEmailModal.value}
        onOpenChange={showEmailModal.setValue}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Email to Communications</DialogTitle>
            <DialogDescription>
              To import emails into the ACCURE communications system, forward
              them to:
            </DialogDescription>
          </DialogHeader>
          <div className="my-4 flex items-center justify-between rounded-md border border-gray-100 bg-gray-50 p-3">
            <code className="font-mono text-sm text-gray-700">{email}</code>
            <Button
              variant="ghost"
              size="sm"
              className="size-7 p-0 text-gray-500"
              onClick={copyEmail}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-copy"
              >
                <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
              </svg>
              <span className="sr-only">Copy email address</span>
            </Button>
          </div>
          <p className="text-sm text-gray-600">
            Once forwarded, the email will be automatically processed and added
            to your communications list.
          </p>
          <DialogFooter>
            <Button onClick={showEmailModal.setFalse}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Meeting Import Modal */}
      <Dialog
        open={showMeetingModal.value}
        onOpenChange={showMeetingModal.setValue}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Meeting to Communications</DialogTitle>
            <DialogDescription>
              To import meetings into the ACCURE communications system, invite
              this email address to your calendar event:
            </DialogDescription>
          </DialogHeader>
          <div className="my-4 flex items-center justify-between rounded-md border border-gray-100 bg-gray-50 p-3">
            <code className="font-mono text-sm text-gray-700">{email}</code>
            <Button
              variant="ghost"
              size="sm"
              className="size-7 p-0 text-gray-500"
              onClick={copyEmail}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-copy"
              >
                <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
              </svg>
              <span className="sr-only">Copy email address</span>
            </Button>
          </div>
          <p className="text-sm text-gray-600">
            Once invited, the meeting will be automatically added to your
            communications list with all relevant details.
          </p>
          <DialogFooter>
            <Button onClick={showMeetingModal.setFalse}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
